/**
 * WeeklyPlanView — pure presentational component for the weekly plan tab.
 *
 * Structure (per reference site samuel-samano.vercel.app):
 *   1. Header: "Plan Nutricional" title + "Última actualización" badge
 *   2. EstrategiaNutricional section
 *   3. HorariosRendimiento card
 *   4. DayTabs (Lunes → Domingo)
 *   5. DayPlanCard for the currently active day
 *
 * All dynamic values via React text bindings — no dangerouslySetInnerHTML.
 * No native dialogs.
 * Responsive: single column on mobile, consistent max-width container.
 */

import { useState } from "react";
import type { WeeklyPlan } from "@autodieta/shared";
import { EstrategiaNutricional } from "./EstrategiaNutricional.js";
import { HorariosRendimiento } from "./HorariosRendimiento.js";
import { DayTabs } from "./DayTabs.js";
import type { WeekdayIndex } from "./DayTabs.js";
import { DayPlanCard } from "./DayPlanCard.js";
import { PANEL_ID_PREFIX, TAB_ID_PREFIX } from "./DayTabs.js";

// ---------------------------------------------------------------------------
// Day labels
// ---------------------------------------------------------------------------

const DAY_LABELS: readonly string[] = [
  "Lunes",
  "Martes",
  "Miércoles",
  "Jueves",
  "Viernes",
  "Sábado",
  "Domingo",
];

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface WeeklyPlanViewProps {
  plan: WeeklyPlan;
}

// ---------------------------------------------------------------------------
// WeeklyPlanView
// ---------------------------------------------------------------------------

export function WeeklyPlanView({ plan }: WeeklyPlanViewProps): JSX.Element {
  const [activeDay, setActiveDay] = useState<WeekdayIndex>(0);

  const formattedDate = formatDate(plan.createdAt);
  const activeDayPlan = plan.days[activeDay];
  const dayLabel = DAY_LABELS[activeDay];

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <header className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-xl font-bold text-zinc-100">Plan Nutricional</h1>
        <span className="rounded-full border border-zinc-700 bg-zinc-800 px-3 py-1 text-xs text-zinc-400">
          Última actualización: {formattedDate}
        </span>
      </header>

      {/* Estrategia Nutricional section */}
      <EstrategiaNutricional />

      {/* Horarios y Rendimiento card */}
      <HorariosRendimiento />

      {/* Day tabs */}
      <DayTabs activeDay={activeDay} onDaySelect={setActiveDay} />

      {/* Active day panel */}
      {activeDayPlan !== undefined ? (
        <DayPlanCard
          day={activeDayPlan}
          dayLabel={dayLabel ?? "Lunes"}
          panelId={`${PANEL_ID_PREFIX}-${activeDay}`}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("es-AR", {
      year: "numeric",
      month: "long",
      day: "numeric",
      timeZone: "UTC",
    });
  } catch {
    return iso;
  }
}

export { TAB_ID_PREFIX };
